import { trackPageView } from '@finn-no/pulse-sdk';
import type { GetServerSideProps, NextPage } from 'next';
import Script from 'next/script';
import { useEffect } from 'react';
import { fetchVariants } from 'shared/experiment/variants';
import { SimpleHead } from 'shared/page/Head';
import type { SspContext } from 'shared/types';
import { config } from '@/config';
import { ExperimentProvider } from '@/hooks/experiments';
import PlatformSelectionPage from '@/scenes/platformSelectionPage/PlatformSelectionPage';
import { runAllMiddlewares } from '@/server/middleware';

type AdInsertionProps = {
    itemId?: string;
    error?: string;
    experiments: { [key: string]: string };
};

const AdInsertion: NextPage<AdInsertionProps> = ({ itemId, error, experiments }) => {
    useEffect(() => {
        trackPageView({
            object: {
                type: 'Page',
                pageType: 'product_description',
                contentId: itemId ?? null, //set for republishing
                category: 'realestate > realestate-lettings-external',
            },
            vertical: {
                name: 'realestate',
                subVertical: 'lettings',
            },
        });
    }, [itemId]);

    const description = 'Publiser din annonse på enten Finn eller Qasa';

    return (
        <>
            <SimpleHead title="FINN eiendom" canonical="https://www.finn.no/realestate/lettings/adinsertion" description={description} />
            <Script
                id="google-pcd-tag"
                src="https://pagead2.googlesyndication.com/pagead/js/pcd.js"
                data-audience-pixel="dc_iu=/23167417479/DFPAudiencePixel;dc_seg=9328601369"
                strategy="afterInteractive"
            />
            <ExperimentProvider experiments={experiments}>
                <PlatformSelectionPage itemId={itemId} error={error} />
            </ExperimentProvider>
        </>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ req, res, query }: SspContext) => {
    const { itemId, error, variant } = query;
    await runAllMiddlewares(req, res, query);

    let experiments = {};

    // Use query param variant if provided, otherwise fetch from experiments API
    if (variant && typeof variant === 'string' && (variant === 'control' || variant === 'treatment')) {
        experiments = {
            'travel-qasa-platform-selector-matrix': variant,
        };
    } else {
        experiments =
            (await fetchVariants(req, res, 'travel-lotr-web', config.get('experimentsApiUrl'), ['travel-qasa-platform-selector-matrix'])) ??
            {};
    }

    return {
        props: {
            hasLegalBasis: res.locals?.hasLegalBasis ?? false,
            unleash: res.locals?.unleash ?? null,
            experiments,
            itemId: itemId ?? null,
            error: error ?? 'false',
        },
    };
};

export default AdInsertion;
